<template>
  <div>
    <teleport to=".start-form-progress .vue-component">
      <progress-bar />
    </teleport>

    <form ref="form" class="d-flex flex-column">
      <component :is="componentForField(field)" v-for="field of currentlyShownFields" :key="field" :ref="field" :field="field" />

      <div class="btn-container">
        <form-row>
          <div class="d-grid">
            <input ref="dummy" class="dummy-input" @focus="handleDummyFocus">
            <submit-button ref="submit" :label="nextFieldButtonLabel" :submit-in-progress="submitInProgress"
                           class="submit-button" @click="submitIfReady(submit)" />
          </div>
        </form-row>
      </div>
    </form>

    <authenticate-to-publish-modal :title="i18n.t('member.petitions.start.sign_in_to_publish')"
                                   :description="i18n.t('member.petitions.start.sign_in_explanation')"
                                   :confirmation-warning-message="i18n.t('member.petitions.start.authenticate_to_publish_modal.email_confirmation_warning')" />

    <awaiting-approval-modal v-if="displayAwaitingApprovalModal" campaign-type="petition" />
  </div>
</template>

<script>
import AuthenticateToPublishModal from '@/campaign-form-shared/authenticate-to-publish-modal'
import AwaitingApprovalModal from '@/campaign-form-shared/awaiting-approval-modal'
import FormRow from '@/campaign-form-shared/form-row'
import LocationField from './location-field'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { nextTick } from 'vue'
import PartnershipField from './partnership-field'
import ProgressBar from './progress-bar'
import StaffLedCampaignField from '@/campaign-form-shared/staff-led-campaign-field'
import StringField from './string-field'
import SubmitButton from '@/components/buttons/submit-button'
import TextField from './text-field'
import smoothscroll from 'smoothscroll-polyfill'
import WaitUntilGeocodedForSubmit from '../../mixins/wait-until-geocoded-for-submit'

export default {
  components: { AuthenticateToPublishModal, AwaitingApprovalModal, FormRow, LocationField, PartnershipField,
                ProgressBar, StaffLedCampaignField, StringField, SubmitButton, TextField },

  mixins: [ WaitUntilGeocodedForSubmit ],

  computed: {
    ...mapState(['displayAwaitingApprovalModal', 'extraParameters', 'submitInProgress', 'submitUrl']),
    ...mapGetters(['contentForNextField', 'currentlyShownFields', 'firstFieldWithError',
                   'lastShownField', 'onFirstField', 'onLastField']),
    // WaitUntilGeocodedForSubmit mixin expects a pendingGeocoding property to exist, so we alias the getter
    ...mapGetters({pendingGeocoding: 'pendingGeocodingOnNextSubmit'}),

    nextFieldButtonLabel() {
      if (this.onLastField) {
        return this.i18n.t('buttons.publish')
      } else {
        return this.i18n.t('member.petitions.start.next_step', {next_step: this.contentForNextField.heading})
      }
    }
  },

  watch: {
    lastShownField(fieldName) {
      // Avoid scrolling to first field on page load
      if (this.onFirstField) {
        return
      }

      nextTick(() => {
        this.$refs[fieldName][0].focus()
        this.$refs[fieldName][0].scrollIntoView()
      })
    }
  },

  mounted() {
    smoothscroll.polyfill()
  },

  methods: {
    ...mapActions(['submitField']),
    ...mapMutations(['setValue']),

    submit() {
      const that = this

      this.submitField()
        .then((submitResult) => {
          // If submit didn't succeed due to validation errors scroll up to field
          // with the error and focus on it
          if (!submitResult) {
            const firstFieldWithError = that.$store.getters.firstFieldWithError
            nextTick(() => {
              that.$refs[firstFieldWithError][0].scrollIntoView()
              that.$refs[firstFieldWithError][0].focus()
            })
          }
        })
    },

    // Hack so that if user uses tab or or the "down" button on iOS keyboard,
    // we handle that and make as if they clicked the button to proceed to the next step.
    // We are unable to handle the clicking of the "down" button directly.
    handleDummyFocus() {
      // If the button is a "Next" button, click the button to submit instead.
      // If the button is the "Publish" button, don't click it, but focus it.
      if (!this.onLastField) {
        this.$refs.submit.click()
      }
      else {
        this.$refs.submit.focus()
      }
    },

    componentForField(field) {
      switch (field) {
      case 'partnershipId':
        return PartnershipField
      case 'title':
      case 'who':
        return StringField
      case 'rich_what':
      case 'rich_why':
        return TextField
      case 'location':
        return LocationField
      case 'staffLedCampaign':
        return StaffLedCampaignField
      default:
        throw `invalid field ${field}`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~common_styles/breakpoints" as breakpoints;

.btn-container {
  position: sticky;
  bottom: 20px;
  margin-top: 40vh;

  .submit-button {
    text-transform: none;
    height: 56px;

    @include breakpoints.screen-mobile-only {
      height: 50px;
    }
  }

  @supports (-webkit-touch-callout: none) {
    // CSS specific to iOS devices To make the button show up correctly with keyboard up on iOS.
    margin-top: unset;
  }
}

.dummy-input {
  width: 0;
  height: 0;
  opacity: 0;
}
</style>
